import { Link } from '@tanstack/react-router'
import { ArrowDownLeft, ArrowUpRight } from 'lucide-react'
import { Skeleton } from 'skullet/react'
import { create } from '@bufbuild/protobuf'
import { timestampFromDate } from '@bufbuild/protobuf/wkt'

import {
  EmailMessageDirection,
  EmailParticipantSchema,
  EmailThreadLiteSchema,
  type EmailThreadLite,
} from '@/gen/cortado/v1/emails_pb'
import { ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { useHasEmailInboxFeature } from '@/hooks/use-has-feature'
import { formatShortDate } from '@/lib/date-utils'
import { cn } from '@/lib/utils'
import { useEmailThreadsByEntity } from '@/stores/emails.store'

/** The thread's customer-side participant: the first non-"user" one, else the first. */
function threadParticipant(thread: EmailThreadLite): string {
  const participant = thread.participants.find((entry) => entry.type !== 'user') ?? thread.participants[0]
  return participant?.name || participant?.email || ''
}

const PAGE_SIZE = 20

const SAMPLE_THREADS: EmailThreadLite[] = [
  { subject: 'Re: Menu options for the reception', preview: 'Thanks, the second option looks perfect for us.' },
  { subject: 'Quote for your June event', preview: 'Here is the quote we discussed on our call.' },
].map(({ subject, preview }, i) =>
  create(EmailThreadLiteSchema, {
    id: `sample-${i}`,
    subject,
    lastMessagePreview: preview,
    lastMessageAt: timestampFromDate(new Date(2026, 0, i + 1)),
    lastMessageDirection: EmailMessageDirection.INBOUND,
    participants: [create(EmailParticipantSchema, { name: 'Jordan Rivera', email: 'jordan@example.com', type: 'contact' })],
  }),
)

/**
 * The job's conversations: its inquiry's, which roll up the job's quotes,
 * booking and invoices (the same list as the lead page's Conversations tab).
 * Only fetched with an inquiry and the Connected inbox (EmailInbox) add-on;
 * otherwise the list stays empty.
 */
export function useJobEmailThreads(inquiryId: string | undefined) {
  const { hasFeature: hasEmailInbox } = useHasEmailInboxFeature()
  return useEmailThreadsByEntity(ParentTypeEnum.PARENT_TYPE_INQUIRY, inquiryId ?? '', {
    limit: PAGE_SIZE,
    enabled: Boolean(inquiryId) && hasEmailInbox,
  })
}

interface JobEmailsPanelProps {
  jobId: string
  inquiryId?: string
  threads: EmailThreadLite[]
  isLoading: boolean
}

/**
 * The rail's Emails card list: the job's conversations (its inquiry's, rolled
 * up with the job's quotes, booking and invoices). The Activity card's Emails
 * tab opens threads inline instead (`JobActivityEmails`). The shared Inbox page is hidden, so
 * each row opens the job's lead page on its Conversations tab.
 */
export function JobEmailsPanel({ jobId, inquiryId, threads, isLoading }: JobEmailsPanelProps) {
  return (
    <Skeleton
      data={isLoading ? null : threads}
      sample={SAMPLE_THREADS}
      label="Loading emails"
      empty={!isLoading && (threads.length === 0 || !inquiryId)}
      emptyMessage={<p className="px-6 py-4 text-sm text-muted-foreground">No emails yet.</p>}
    >
      {(threads) => (
        <ul className="divide-y">
          {threads.map((thread) => {
            const Icon = thread.lastMessageDirection === EmailMessageDirection.OUTBOUND ? ArrowUpRight : ArrowDownLeft
            const participant = threadParticipant(thread)
            return (
              <li key={thread.id}>
                <Link
                  to="/pipeline/$id/leads/$leadId"
                  params={{ id: jobId, leadId: inquiryId ?? '' }}
                  search={{ tab: 'conversations' }}
                  className="flex items-start gap-3 px-6 py-3 hover:bg-muted/50"
                >
                  <Icon
                    aria-label={thread.lastMessageDirection === EmailMessageDirection.OUTBOUND ? 'Sent' : 'Received'}
                    className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className={cn('truncate text-sm', thread.isUnread ? 'font-semibold' : 'font-medium')}>
                        {thread.subject || '(no subject)'}
                      </span>
                      {thread.isUnread && (
                        <span role="img" aria-label="Unread" className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                      )}
                    </div>
                    {thread.lastMessagePreview && (
                      <div className="truncate text-xs text-muted-foreground">{thread.lastMessagePreview}</div>
                    )}
                    {participant && <div className="truncate text-xs text-muted-foreground">{participant}</div>}
                  </div>
                  {thread.lastMessageAt && (
                    <span className="shrink-0 font-mono text-xs text-muted-foreground">
                      {formatShortDate(thread.lastMessageAt)}
                    </span>
                  )}
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </Skeleton>
  )
}
